/**
 * Performance Tab Component - Real supplier performance metrics
 *
 * CR-5765: Added click-through functionality for KPIs to drill down into
 * underlying records (GRNs, PO Lines, NCRs).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ShieldCheck, Truck, Clock, Info, ExternalLink, AlertTriangle, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { SupplierProfile } from '../../lib/api';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import OTDDrilldownModal from '../../components/drilldown/OTDDrilldownModal';
import OTIFDrilldownModal from '../../components/drilldown/OTIFDrilldownModal';
import { DailyOtifChart } from './DailyOtifChart';
import { PerformanceBreakdownCards } from './PerformanceBreakdownCards';
import { formatNumber } from './performanceFormatting';
import { PerformancePeriodFilter } from './PerformancePeriodFilter';
import { usePerformanceWindow } from './usePerformanceWindow';
import {
  DEFAULT_PERFORMANCE_PERIOD,
  PerformancePeriod,
  describePerformancePeriod,
  resolvePerformanceWindow,
} from './performancePeriods';

interface PerformanceTabProps {
  supplier: SupplierProfile;
}

export function PerformanceTab({ supplier: sharedProfile }: PerformanceTabProps) {
  const navigate = useNavigate();
  const qualityLabel = useQualityMeasureLabel();

  // CR-5563: the selected KPI reporting period. Defaults to Last 12 Months per
  // the ticket — note this DELIBERATELY changes OTIF and quality on first load,
  // which were previously all-time while OTD alone was trailing 12 months.
  const [period, setPeriod] = useState<PerformancePeriod>(DEFAULT_PERFORMANCE_PERIOD);
  // Bumped by "Try again" so a failed window can be re-requested without the
  // user having to pick a different period and come back.
  const [reloadToken, setReloadToken] = useState(0);
  // Named periodWindow, not window: a local called `window` shadows the
  // global one for the whole component.
  const periodWindow = useMemo(() => resolvePerformanceWindow(period), [period]);
  const periodCaption = useMemo(
    () => describePerformancePeriod(period, periodWindow),
    [period, periodWindow],
  );

  // Fetched separately from the shared profile so the window cannot leak into
  // Company Overview, Orders, Quality or Credit Scores — see usePerformanceWindow.
  const {
    profile: supplier,
    loading: windowLoading,
    error: windowError,
  } = usePerformanceWindow(sharedProfile.id, periodWindow, reloadToken);

  // CR-5765: Drilldown modal states
  const [otifDrilldownOpen, setOtifDrilldownOpen] = useState(false);
  const [otdDrilldownOpen, setOtdDrilldownOpen] = useState(false);
  const [otdInitialTab, setOtdInitialTab] = useState<'on_time' | 'early' | 'late' | 'total'>('total');
  const [otifInitialTab, setOtifInitialTab] = useState<'failed' | 'arrears' | 'positive' | 'total'>('total');

  // Supplier filter for drilldowns.
  // CR-5563: carries the selected window so a drilldown opened from a card
  // lists the same records the card counted. Memoised because it is the
  // `filters` dependency of both modals' fetch effects — rebuilt inline it
  // re-fetched an OPEN drilldown on every parent render.
  // The company id is taken from the shared profile: it identifies the
  // supplier and does not vary with the window.
  const supplierFilter = useMemo(
    () => ({
      supplier: sharedProfile.company,
      date_from: periodWindow.dateFrom,
      date_to: periodWindow.dateTo,
    }),
    [sharedProfile.company, periodWindow.dateFrom, periodWindow.dateTo],
  );

  // CR-5765: Navigation handlers
  // CR-5766: Use supplier ID (not name) for URL param - API filters by ID
  const navigateToNCRs = (liability?: '1' | '2') => {
    const params = new URLSearchParams();
    params.set('supplier', String(sharedProfile.company));
    if (liability) {
      params.set('liability', liability);
    }
    // CR-5563: /quality reads date_from / date_to from the URL (CR-6310 added
    // it for exactly this drill-through), so the NCR list opens on the same
    // window as the card that was clicked.
    params.set('date_from', periodWindow.dateFrom);
    params.set('date_to', periodWindow.dateTo);
    navigate(`/quality?${params.toString()}`);
  };

  // CR-5766: Use supplier ID for exact matching (not name which uses icontains)
  // Navigate to PO Lines tab (for "Total PO Lines" metric)
  // Add status_tab=all to show ALL lines (not just Open)
  //
  // CR-5563: carries the selected window. PO Lines already maps date_from /
  // date_to onto its Original Due Date filter (usePOLinesState.ts:371-397,
  // added by CR-6310 for exactly this drill-through), which is the same date
  // the OTIF figures are keyed on — so the list matches the card.
  const navigateToPOLines = () => {
    const params = new URLSearchParams({
      tab: 'po_lines',
      supplier: String(sharedProfile.company),
      status_tab: 'all',
      date_from: periodWindow.dateFrom,
      date_to: periodWindow.dateTo,
    });
    navigate(`/purchasing?${params.toString()}`);
  };

  // CR-5766: Navigate to PO Headers tab (for "Total Orders" metric)
  // Use supplier ID for exact matching, status_tab controls which orders to show
  //
  // CR-5563: the window lands on PO Headers' issue-date filter — the same
  // anchor the windowed total_orders / completed_orders counts use, so the
  // list and the KPI agree.
  const navigateToPOHeaders = (statusTab: 'all' | 'closed' = 'all') => {
    const params = new URLSearchParams({
      tab: 'po_headers',
      supplier: String(sharedProfile.company),
      status_tab: statusTab,
      date_from: periodWindow.dateFrom,
      date_to: periodWindow.dateTo,
    });
    navigate(`/purchasing?${params.toString()}`);
  };

  const openOtdDrilldown = (tab: 'on_time' | 'early' | 'late' | 'total' = 'total') => {
    setOtdInitialTab(tab);
    setOtdDrilldownOpen(true);
  };

  const openOtifDrilldown = (tab: 'failed' | 'arrears' | 'positive' | 'total' = 'total') => {
    setOtifInitialTab(tab);
    setOtifDrilldownOpen(true);
  };

  const periodControl = (
    <PerformancePeriodFilter
      period={period}
      periodWindow={periodWindow}
      loading={windowLoading}
      onChange={setPeriod}
    />
  );

  // The window failed: say so rather than presenting another period's figures
  // as if they were this one's.
  if (windowError) {
    return (
      <div className="space-y-4">
        {periodControl}
        <Card>
          <CardContent className="py-12 text-center space-y-2">
            <AlertTriangle className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm font-medium">Could not load performance for this period</p>
            <p className="text-xs text-muted-foreground">{windowError}</p>
            <Button variant="outline" size="sm" onClick={() => setReloadToken(t => t + 1)}>
              Try again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // First load for this supplier — skeleton rather than the undated numbers.
  if (!supplier) {
    return (
      <div className="space-y-4">
        {periodControl}
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Safe below the guards above: `supplier` is the windowed profile.
  // null = N/A (nothing closed or overdue to score, METRIC-DEFINITIONS Q13)
  const otifPercent = supplier.otif_score_percentage ?? null;
  const otdPercent = supplier.on_time_delivery_rate ?? 0;
  const totalPpm = supplier.total_ppm ?? 0;

  return (
    <div className="space-y-4">
      {periodControl}

      {/* CR-6192: Daily OTIF time-series with click-through to PO lines.
          CR-5563: follows the period control rather than carrying its own
          30/90/180 toggle — two date controls on one screen, with different
          options and different defaults, was the confusing option. */}
      <DailyOtifChart supplier={supplier} dateFrom={periodWindow.dateFrom} dateTo={periodWindow.dateTo} />

      {/* Top-level KPI cards */}
      <div className={`grid gap-4 md:grid-cols-3 ${windowLoading ? 'opacity-60 transition-opacity' : ''}`}>
        {/* OTIF Score - CR-5765: Clickable */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow group"
          onClick={() => openOtifDrilldown('total')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openOtifDrilldown('total')}
          title="Click to view OTIF details"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <Truck className="h-4 w-4 text-muted-foreground" />
              OTIF Score
              {/* CR-5948: surface the in-codebase OTIF rule so users can
                  interpret the number. OTIF is on-time AND in-full —
                  OTIFService only flags is_otif when both hold. Earlier
                  copy claimed the in-full check was unenforced; that was
                  stale (it has been enforced since the window-aware rule). */}
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    Counts PO lines delivered on-time AND in-full.
                    On-time: every receipt lands inside the supplier's
                    OTIF tolerance window (both too-early and late fail).
                    In-full: total quantity received meets or exceeds the
                    quantity ordered.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {otifPercent === null ? 'N/A' : `${formatNumber(otifPercent, 1)}%`}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(supplier.otif_count)} of {formatNumber(supplier.po_line_count)} PO lines
              {/* CR-5563: period comes from the control above. The
                  serializer collapses every supplied window to the string
                  "Custom range", so it cannot caption "Last 30 Days". */}
              <span className="ml-1">· {periodCaption}</span>
            </p>
          </CardContent>
        </Card>

        {/* OTD Rate - CR-5765: Clickable */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow group"
          onClick={() => openOtdDrilldown('total')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openOtdDrilldown('total')}
          title="Click to view OTD details"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-muted-foreground" />
              OTD Rate
              <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(otdPercent, 2)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(supplier.ontime_count ?? 0)} on-time of {formatNumber(supplier.grn_count)} GRNs
              {/* CR-5563: period comes from the control above. The
                  serializer collapses every supplied window to the string
                  "Custom range", so it cannot caption "Last 30 Days". */}
              <span className="ml-1">· {periodCaption}</span>
            </p>
          </CardContent>
        </Card>

        {/* Total PPM - CR-5765: Clickable, navigates to NCRs */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow group"
          onClick={() => navigateToNCRs()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigateToNCRs()}
          title="Click to view NCRs"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              Total {qualityLabel}
              <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(totalPpm, 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(supplier.reject_qty)} rejects / {formatNumber(supplier.qty_received)} received
              {/* CR-5563: period comes from the control above. The
                  serializer collapses every supplied window to the string
                  "Custom range", so it cannot caption "Last 30 Days". */}
              <span className="ml-1">· {periodCaption}</span>
            </p>
          </CardContent>
        </Card>

      </div>

      {/* Detailed breakdown cards */}
      <PerformanceBreakdownCards
        supplier={supplier}
        qualityLabel={qualityLabel}
        otifPercent={otifPercent}
        otdPercent={otdPercent}
        dimmed={windowLoading}
        onNavigateToNCRs={navigateToNCRs}
        onNavigateToPOLines={navigateToPOLines}
        onNavigateToPOHeaders={navigateToPOHeaders}
        onOpenOtdDrilldown={openOtdDrilldown}
        onOpenOtifDrilldown={openOtifDrilldown}
      />

      {/* CR-5765: Drilldown Modals */}
      <OTIFDrilldownModal
        isOpen={otifDrilldownOpen}
        onClose={() => setOtifDrilldownOpen(false)}
        filters={supplierFilter}
        contextLabel={supplier.company_name}
        initialTab={otifInitialTab}
      />

      <OTDDrilldownModal
        isOpen={otdDrilldownOpen}
        onClose={() => setOtdDrilldownOpen(false)}
        filters={supplierFilter}
        contextLabel={supplier.company_name}
        initialTab={otdInitialTab}
      />

    </div>
  );
}
